// Imports => React
import { useEffect, useMemo } from 'react';
import { withStore } from '@stores';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';

// Imports => Constants
import { KEYS, THEMES } from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
  AcIsArray,
  AcSaveState,
  AcSanitize,
} from '@opp/ui-kit';

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcDivider,
  AcRichContent,
  AcIcon,
  AcInitials,
  AcLoaderInline,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";

const _CLASSES = {
  MAIN: 'ac-merchant-select-modal ac-modal--size-sm',
  CONTENT: 'ac-merchant-select-modal__content',
  LIST: {
    MAIN: 'ac-merchant-select-modal__list',
    ITEM: 'ac-merchant-select-modal__list__item',
    ACTIVE: 'ac-merchant-select-modal__list__item--active',
  },
  DETAILS: {
    MAIN: 'ac-merchant-select-modal__details',
    NAME: 'ac-merchant-select-modal__details__name',
    PARTNER: 'ac-merchant-select-modal__details__partner',
  },
};

const AcMerchantSelectModal = ({
  store: { profile },
  current = null,
  collection = null,
  data = null,
  callback,
  t
}) => {
  useEffect(() => {}, []);

  const hasProfiles = useMemo(() => {
    if (!AcIsSet(current) || !AcIsSet(collection) || !AcIsArray(collection))
      return false;
    const filtered =
            collection && collection.filter((item) => item.uid !== current.uid);

    return AcIsSet(filtered) && AcIsArray(filtered) && filtered.length > 0;
  }, [current, collection]);

  const handleProfileSwitch = (event, uid) => {
    if (event && event.persist) event.persist();
    if (event && event.preventDefault) event.preventDefault();

    if (!AcIsSet(uid)) return;
    if (uid === current.uid) return;

    AcSaveState(KEYS.XUSR_TOKEN, uid);
    if (AcIsSet(callback)) callback(event, uid);
  };

  const getMerchantSelectPartnerNameClassNames = useMemo(() => {
    return clsx(_CLASSES.DETAILS.PARTNER);
  }, []);

  const getMerchantSelectNameClassNames = useMemo(() => {
    return clsx(_CLASSES.DETAILS.NAME);
  }, []);

  const getMerchantSelectDetailsClassNames = useMemo(() => {
    return clsx(_CLASSES.DETAILS.MAIN);
  }, []);

  const getMerchantSelectListItemActiveClassNames = useMemo(() => {
    return clsx(_CLASSES.LIST.ITEM, _CLASSES.LIST.ACTIVE);
  }, []);

  const getMerchantSelectListItemClassNames = useMemo(() => {
    return clsx(_CLASSES.LIST.ITEM);
  }, []);

  const getMerchantSelectListClassNames = useMemo(() => {
    return clsx(_CLASSES.LIST.MAIN);
  }, []);

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN]);
  }, []);

  const renderCurrentMerchantProfile = useMemo(() => {
    if (!AcIsSet(current)) return null;

    const item = current;
    let result = null;

    if (AcIsSet(item)) {
      const { partner } = data;

      let partner_name = AcIsSet(partner) && partner.name ? partner.name : null;
      let merchant_name = item.name;

      result = (
        <li
          key={item.uid}
          className={getMerchantSelectListItemActiveClassNames}
        >
          <AcInitials
            theme={THEMES.PITCH}
            firstname={merchant_name}
            title={merchant_name}
          />
          <div className={getMerchantSelectDetailsClassNames}>
            <span
              className={getMerchantSelectNameClassNames}
            >
              {merchant_name}
            </span>
            {partner_name && (
              <span

                className={getMerchantSelectPartnerNameClassNames}
              >
                {partner_name}
              </span>
            )}
          </div>
        </li>
      );
    }

    return <ul className={getMerchantSelectListClassNames}>{result}</ul>;
  }, [
    current,
    getMerchantSelectListClassNames,
    getMerchantSelectListItemClassNames,
  ]);

  const renderMerchantProfiles = useMemo(() => {
    if (!AcIsSet(current)) return null;
    if (!AcIsSet(collection)) return null;

    const len = collection.length;
    let n = 0;
    let result = [];
    let active = null;

    for (n; n < len; n++) {
      const item = collection[n];

      if (current.uid === item.uid) continue;

      const { partner } = item;

      let partner_name = AcIsSet(partner) && partner.name ? partner.name : null;
      let merchant_name = item.name;

      const object = (
        <li
          onClick={(event) => handleProfileSwitch(event, item.uid)}
          key={item.uid}
          className={getMerchantSelectListItemClassNames}
        >
          <AcInitials
            theme={THEMES.PITCH}
            firstname={merchant_name}
            title={merchant_name}
          />
          <div className={getMerchantSelectDetailsClassNames}>
            <span
              className={getMerchantSelectNameClassNames}
            >
              {merchant_name}
            </span>
            {partner_name && (
              <span
                className={getMerchantSelectPartnerNameClassNames}
              >
                {partner_name}
              </span>
            )}
          </div>
        </li>
      );

      result.push(object);
    }

    result = result.sort((x, y) =>
      x.uid == current.uid ? -1 : y.uid == current.uid ? 1 : 0
    );

    return <ul className={getMerchantSelectListClassNames}>{result}</ul>;
  }, [
    current,
    collection,
    getMerchantSelectListClassNames,
    getMerchantSelectListItemClassNames,
  ]);

  return (
    <div className={getStyleClassNames}>
      <div className={getContentClassNames}>
        <AcContainer fluid className={'h-padding-y-10'}>
          <AcRow>
            <AcColumn>
              <AcRichContent content={`<p>${t('your-active-platform', 'Your active platform', {ns: 'common'})}</p>`} />
            </AcColumn>
          </AcRow>

          <AcRow>
            <AcColumn>{renderCurrentMerchantProfile}</AcColumn>
          </AcRow>

          {hasProfiles && (
            <>
              <AcRow className={'h-margin-top-20 h-margin-bottom-15'}>
                <AcColumn>
                  <AcDivider theme={THEMES.EGGWHITE} />
                </AcColumn>
              </AcRow>

              <AcRow>
                <AcColumn>
                  <AcRichContent
                    content={
                      `<p>${t('switch-platform', 'Switch to another platform', {ns: 'common'})}</p><em class="h-text--size-small">${t('switch-platform-sub', 'You can always switch back to your current platform.', {ns: 'common'})}</em>`
                    }
                  />
                </AcColumn>
              </AcRow>

              <AcRow>
                <AcColumn>{renderMerchantProfiles}</AcColumn>
              </AcRow>
            </>
          )}

          {profile.is_busy && <AcLoaderInline loading cover />}
        </AcContainer>
      </div>
    </div>
  );
};

export default withTranslation(['common'])(withStore(observer(AcMerchantSelectModal)));
